import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiService } from "../api/apiService";
import { useAuth } from "./useAuth";
import { pobierzZPamieci, tozsamoscKonta, KLUCZE_POWLOKI } from "./usePamiecPowloki";

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * MOJE MODUŁY OBSŁUGI (`GET /api/ticket-modules/my`) — JEDNO WEJŚCIE DO JEDNEJ PAMIĘCI.
 *
 * ── CO BYŁO ZŁE (pomiar z produkcji, dziennik usługi, 3 dni) ──────────────────────────────────
 * `GET /api/ticket-modules/my` to **243 wywołania przy ~140 wejściach do aplikacji** — prawie dwa
 * razy na wejście, a lista modułów obsługi zmienia się w `/admin/categories`, nie w trakcie
 * klikania po menu. Pamięć powłoki (`usePamiecPowloki`) powstała właśnie po to, ale korzystał
 * z niej WYŁĄCZNIE nagłówek. Audyt zmierzył, że pozostałe CZTERY miejsca pytały serwer wprost:
 *   • `TicketsPage` — DWA osobne efekty w jednym pliku: jeden liczył „ile mam modułów" (lustro
 *     bramki filtra domen, B-5), drugi rozstrzygał, czyja jest kolejka (G-13a). Wejście na
 *     `/obsluga-hr/zgloszenia` kosztowało więc DWA identyczne zapytania,
 *   • `ItManagementPage` — rozstrzygnięcie modułu landingu,
 *   • `HrManagementPage` — przycisk „Zgłoszenia" + lista spraw na dole strony.
 * Pięciu pytających = do sześciu identycznych zapytań na jedno wejście, przy czym nagłówek
 * i strona montują się w tej samej chwili, więc nawet sklejanie żądań „w locie" z `apiService`
 * łapało z tego tylko część (strona montuje się po nagłówku, gdy tamto już wróciło).
 *
 * ── CO TO ZMIENIA ─────────────────────────────────────────────────────────────────────────────
 * Wszyscy pytają TĘ SAMĄ pamięć, pod TYM SAMYM kluczem (`KLUCZE_POWLOKI.MODULY_OBSLUGI`)
 * i z tożsamością liczoną JEDNĄ funkcją (`tozsamoscKonta`) — bo rozjazd w którymkolwiek z tych
 * trzech miejsc oznacza pudło w pamięci i ciche wysłanie drugiego zapytania.
 *
 * ── CZEGO TO NIE ZMIENIA (to jest najważniejsze zdanie w tym pliku) ───────────────────────────
 * 🔴 ZAWĘŻENIE LISTY DO MODUŁU ZOSTAJE NIETKNIĘTE. Kolejka i landing wysyłają `moduleCode`, bo
 * bez niego serwer oddaje CAŁY zakres kolejki zalogowanej osoby — a to właśnie tą drogą sprawy
 * kadrowe wyciekły do modułu IT („to jest niedopuszczalne !!!", zgłoszenie właściciela 2026-09-13).
 * Ten plik zmienia tylko to, SKĄD strona bierze listę modułów; `ticketModule`, `moduleStatus`
 * i `effectiveModuleCode` liczą się z niej dokładnie jak dotąd. Zapadki
 * `kolejkaITzawezonaDoModulu.wyciekHR` i `rozdzialModulowLanding` mierzą parametr wysłany
 * do serwera i muszą zostać zielone.
 *
 * ── BEZPIECZEŃSTWO: CO Z WYLOGOWANIEM NA WSPÓLNYM STANOWISKU ──────────────────────────────────
 * Lista modułów obsługi to odpowiedź ZALEŻNA OD KONTA (członkostwo w obsadzie desku, dostęp do
 * skrzynki, nadanie imienne). Dlatego:
 *   • wpis zapisuje przy sobie tożsamość właściciela — odczyt innym kontem to PUDŁO, nie trafienie
 *     (przelogowanie w tej samej karcie nie oddaje odpowiedzi poprzednika),
 *   • `sesja-zakonczona` (wylogowanie) i `uprawnienia-zmienione` (cichy /refresh) kasują CAŁĄ
 *     pamięć powłoki — odebranie `TicketView` zdejmuje moduł od razu, a nie po 10 minutach,
 *   • nie ma tu `localStorage` ani `sessionStorage`: pamięć przeglądarki przeżywa wylogowanie.
 * Wszystkie trzy reguły są egzekwowane w `usePamiecPowloki` — ten plik ich nie obchodzi i nie
 * wolno mu ich obejść (np. własnym `Map()` obok).
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * Jedno pobranie listy modułów obsługi na sesję karty (per konto).
 *
 * @param {object}  opcje
 * @param {string|number|null} opcje.tozsamosc identyfikator konta — LICZONY `tozsamoscKonta(user)`
 * @param {boolean} opcje.wymus                pomiń pamięć (przycisk „Spróbuj ponownie", SignalR)
 * @returns {Promise<Array>} lista modułów; obietnica ODRZUCA przy błędzie transportu — każdy
 *          konsument musi sam zdecydować, czy to „brak dostępu" (nigdy!) czy „nie wiem" (tak).
 */
export const pobierzMojeModulyObslugi = ({ tozsamosc = null, wymus = false } = {}) =>
    pobierzZPamieci(KLUCZE_POWLOKI.MODULY_OBSLUGI, {
        pobieracz: () => apiService.getMyTicketModules().then((r) => (Array.isArray(r?.data) ? r.data : [])),
        tozsamosc,
        wymus,
    });

/**
 * Lista modułów obsługi dla ZALOGOWANEGO konta, z pamięci powłoki.
 *
 * @param {object}  opcje
 * @param {boolean} opcje.aktywny czy w ogóle pytać (landing IT nie potrzebuje tej listy — patrz
 *        `ItManagementPage`; nieaktywny hook nie wysyła NIC i zgłasza `znany === false`)
 * @returns {{moduly: Array, znany: boolean, blad: boolean, ladowanie: boolean, odswiez: Function}}
 *   • `moduly` — lista (puste przy błędzie: fail-closed, bo z tej listy wynika dostęp do ekranu),
 *   • `znany`  — czy pytanie się ROZSTRZYGNĘŁO (także niepomyślnie). Bez tego ekran nie umie
 *                odróżnić „jeszcze nie wiem" od „nie ma modułu" i albo miga odmową, albo — gorzej —
 *                pyta o listę zgłoszeń bez kodu modułu,
 *   • `blad`   — rozstrzygnęło się BŁĘDEM transportu. 🔴 Błąd ≠ brak dostępu: agent dostawał przy
 *                restarcie backendu trwałe „Nie masz dostępu do tego modułu" bez drogi ponowienia,
 *   • `odswiez` — wymuszone pobranie z pominięciem pamięci (przycisk „Spróbuj ponownie").
 */
export const useMojeModulyObslugi = ({ aktywny = true } = {}) => {
    const { user } = useAuth() || {};
    const tozsamosc = tozsamoscKonta(user);

    /* `dla` = tożsamość, DO KTÓREJ NALEŻY ten stan.
       ⚠ Bez tego pola wystąpiłoby krótkie okno na WSPÓLNYM STANOWISKU: po zmianie tożsamości efekt
       idzie po nową listę, ale w stanie leży jeszcze lista POPRZEDNIEJ osoby — i do tej jednej tury
       ekran raportowałby `znany === true` z cudzym modułem (czyli kolejka wysłałaby zapytanie
       z `moduleCode` modułu, którego nowa osoba nie obsługuje). Serwer i tak liczy zakres sam, więc
       danych by to nie ujawniło, ale stan byłby NIEPRAWDZIWY — a tu właśnie na stanie stoi decyzja
       „pytać o listę zgłoszeń czy jeszcze nie". Dopóki tożsamości się nie zgadzają, mówimy „nie wiem". */
    const [stan, setStan] = useState({ moduly: [], znany: false, blad: false, dla: null });
    const zywy = useRef(true);
    useEffect(() => {
        zywy.current = true;
        return () => { zywy.current = false; };
    }, []);

    /* Bez `AbortController`: `apiService` nie przyjmuje dziś `signal`, a przerwanie żądania
       ZEPSUŁOBY współdzielenie pobrania (drugi czekający dostałby `AbortError` zamiast danych).
       Odcinamy więc SKUTEK — zapis do stanu po odmontowaniu — a nie samo połączenie. */
    /* 🔴 [H1/G2 2026-09-19] NIE PYTAMY, DOPÓKI NIE WIEMY, KTO PYTA.
       ZMIERZONE (zapadka `__tests__/sufitZadanLandingow.h1`, licznik BRUTTO na `api.get`): wejście
       na `/obsluga-hr` wysyłało `GET /ticket-modules/my` DWA RAZY. Powód nie jest widoczny w kodzie
       stron — jest w kolejności montażu Reacta: efekty dziecka lecą PRZED efektem rodzica, więc
       landing pytał o listę jeszcze zanim `AuthContext` zdążył ustawić konto. Pierwsze pytanie szło
       z tożsamością `null` (wpis „dane globalne"), drugie — już z identyfikatorem konta — było dla
       pamięci PUDŁEM i leciało do serwera po raz drugi.
       Na sieci widać było tylko JEDNO żądanie, bo sklejanie równoległych GET-ów z `apiService`
       doklejało drugie do pierwszego. To jest dokładnie ten przypadek, przed którym ostrzega pamięć
       projektu: sklejanie MASKUJE duplikat, a nie usuwa. Wystarczyłoby, żeby pierwsza odpowiedź
       wróciła przed drugim pytaniem (wolniejsza sieć, cudzy render w międzyczasie) i na serwer szłyby
       dwa realne żądania — a wpis globalny `tozsamosc: null` zostawałby w pamięci karty.
       Wzorzec jest ten sam co w `components/Layout.js` (`if (!idKonta) return;`).
       ⚠ Świadoma konsekwencja: bez znanego konta hook nie rozstrzyga niczego i melduje `ladowanie`
       (nie „brak dostępu"), więc ekran pokazuje stan ładowania zamiast migać odmową. Dla sesji bez
       zalogowania to stan docelowy — takie ekrany i tak są za `ProtectedRoute`. */
    const znamyPytajacego = tozsamosc != null;
    const pytamy = aktywny && znamyPytajacego;

    const wczytaj = useCallback((wymus) => {
        if (!pytamy) return Promise.resolve([]);
        return pobierzMojeModulyObslugi({ tozsamosc, wymus })
            .then((lista) => {
                if (zywy.current) {
                    setStan({ moduly: Array.isArray(lista) ? lista : [], znany: true, blad: false, dla: tozsamosc });
                }
                return lista;
            })
            .catch(() => {
                // fail-closed: brak potwierdzenia = zero modułów, ale z flagą „to był BŁĄD",
                // żeby ekran pokazał ponowienie, a nie odmowę dostępu.
                if (zywy.current) setStan({ moduly: [], znany: true, blad: true, dla: tozsamosc });
                return [];
            });
    }, [pytamy, tozsamosc]);

    useEffect(() => { wczytaj(false); }, [wczytaj]);

    /* [P13a, polecenie właściciela 2026-10-01, W-1] Przełączniki funkcji modułu (np. „Raport daily — moduł”) jadą w tej
       samej liście `GET my`. Zmiana w /admin/categories nadaje `FunkcjeModuluZmiana` → zdarzenie okna
       `funkcje-modulu-zmiana`; wymuszamy pobranie z pominięciem pamięci, inaczej kolejka pokazywałaby wyłączony przycisk
       do końca sesji karty. Nasłuch tylko wtedy, gdy hook w ogóle pyta (landing IT nie wysyła nic). */
    useEffect(() => {
        if (!pytamy) return undefined;
        const naZmiane = () => { wczytaj(true); };
        window.addEventListener("funkcje-modulu-zmiana", naZmiane);
        return () => window.removeEventListener("funkcje-modulu-zmiana", naZmiane);
    }, [pytamy, wczytaj]);

    const odswiez = useCallback(() => wczytaj(true), [wczytaj]);

    const naszStan = pytamy && stan.dla === tozsamosc;
    return {
        moduly: naszStan ? stan.moduly : [],
        znany: naszStan && stan.znany,
        blad: naszStan && stan.blad,
        ladowanie: aktywny && !(naszStan && stan.znany),
        odswiez,
    };
};

/**
 * Rozstrzygnięcie „czyja jest ta kolejka / ten landing" — wspólne dla `TicketsPage`
 * i `ItManagementPage`, bo obie strony miały ten sam efekt przepisany osobno (i obie pytały
 * serwer wprost).
 *
 * @param {object} opcje
 * @param {string|null} opcje.viewModuleCode kod STRONY WBUDOWANEJ modułu (`ViewModuleCode`)
 * @param {string|null} opcje.moduleCode     kod modułu wprost (`Code`) — moduły dynamiczne
 * @param {boolean} opcje.czyPotrzebneRozstrzygniecie `false` = to kolejka/landing IT, nie ma czego
 *        rozstrzygać (status „it", modul `null`) i samo rozstrzygnięcie zapytania nie wymaga
 * @param {boolean} opcje.pytajZawsze pobierz listę TAKŻE wtedy, gdy nie ma czego rozstrzygać.
 *        Potrzebne dokładnie w jednym miejscu: kolejka zgłoszeń liczy z tej listy lustro bramki
 *        filtra domen (B-5), a ta bramka jest używana WŁAŚNIE na kolejce IT. Landing IT tego
 *        nie ustawia — i dlatego nie wysyła ani jednego zapytania o moduły.
 * @returns {{modul: object|null, status: "it"|"loading"|"resolved"|"denied"|"error",
 *           moduly: Array, odswiez: Function}}
 */
export const useRozstrzygnietyModulObslugi = ({
    viewModuleCode = null,
    moduleCode = null,
    czyPotrzebneRozstrzygniecie = Boolean(viewModuleCode || moduleCode),
    pytajZawsze = false,
} = {}) => {
    const { moduly, znany, blad, odswiez } = useMojeModulyObslugi({
        aktywny: czyPotrzebneRozstrzygniecie || pytajZawsze,
    });

    const modul = useMemo(() => {
        if (!czyPotrzebneRozstrzygniecie) return null;
        if (!Array.isArray(moduly)) return null;
        // Kolejność warunków jak w starych efektach: strona wbudowana identyfikuje moduł po
        // `ViewModuleCode`, moduł dynamiczny po `Code`. Nie wolno tego odwrócić — „hrManagement"
        // nie jest kodem modułu, a „hr" nie jest kodem strony wbudowanej.
        return (viewModuleCode
            ? moduly.find((m) => m.viewModuleCode === viewModuleCode)
            : moduly.find((m) => m.code === moduleCode)) || null;
    }, [czyPotrzebneRozstrzygniecie, moduly, viewModuleCode, moduleCode]);

    const status = useMemo(() => {
        if (!czyPotrzebneRozstrzygniecie) return "it";
        if (blad) return "error";      // 🔴 przed „denied": restart backendu to NIE brak dostępu
        if (!znany) return "loading";
        return modul ? "resolved" : "denied";
    }, [czyPotrzebneRozstrzygniecie, blad, znany, modul]);

    return { modul, status, moduly: Array.isArray(moduly) ? moduly : [], odswiez };
};

export default useMojeModulyObslugi;
